import axios from 'axios';
import { getTokens } from './authApi';

/**
 * Global auth interceptor for the DEFAULT axios instance.
 *
 * Several API modules (services/api/*.ts: knowledgeBaseApi, feesApi, dviApi,
 * timeEntriesApi, attachmentExtensionsApi, auditTrailApi, etc.) call bare
 * `axios.get('/api/...')` instead of the configured `api` instance in
 * services/api.ts. Those bare calls never carried the JWT, so every request to
 * an `authenticate`-protected route returned 401 and the feature silently
 * failed (fees, DVI, condition photos, audit trail, time entries, KB pricing).
 *
 * axios.create() instances do NOT inherit interceptors from the default
 * instance, so this only affects bare `axios.*` calls — the `api` / `authApi` /
 * other named clients keep their own interceptors untouched.
 *
 * Scope: the token is attached ONLY for requests to our own backend (relative
 * URLs, or absolute URLs to the API origin / the dev backend) so it is never
 * leaked to a genuinely third-party host.
 */

// REACT_APP_API_URL is like "https://cnfauto.com/api" — derive the origin.
const apiOrigin = (() => {
  try {
    const raw = process.env.REACT_APP_API_URL;
    if (!raw) return '';
    return new URL(raw, window.location.origin).origin;
  } catch {
    return '';
  }
})();

function targetsOwnApi(url?: string): boolean {
  if (!url) return false;
  // Relative URL → same origin as the app → our backend (via CRA proxy / nginx).
  if (!/^https?:\/\//i.test(url)) return true;
  // Absolute URL: compare PARSED ORIGINS for exact equality. A prefix/substring
  // match (url.startsWith(apiOrigin)) is bypassable — e.g. apiOrigin
  // "https://cnfauto.com" also prefixes "https://cnfauto.com.evil.com", which
  // would leak the JWT to an attacker host.
  try {
    const reqOrigin = new URL(url).origin;
    if (apiOrigin && reqOrigin === apiOrigin) return true;
    if (reqOrigin === window.location.origin) return true;
    // Dev backend hardcoded in a few legacy callers.
    if (reqOrigin === 'http://localhost:3001') return true;
  } catch {
    return false;
  }
  return false;
}

let installed = false;

export function setupAxiosAuth(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    if (targetsOwnApi(config.url)) {
      const tokens = getTokens();
      if (tokens?.accessToken) {
        config.headers = config.headers || {};
        // Don't clobber an explicitly-set header.
        if (!(config.headers as Record<string, unknown>).Authorization) {
          (config.headers as Record<string, unknown>).Authorization = `Bearer ${tokens.accessToken}`;
        }
      }
    }
    return config;
  });
}
